import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Checkbox,
  Flex,
  IconButton,
  Input,
  SimpleGrid,
  Spinner,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react';
import { HiTrash } from 'react-icons/hi2';
import { Button as ButtonComponent } from '@themes/aegix';
import { DrawerComponent } from '../ui/drawerComponent';
import type {
  PlanApplicableSite,
  ReunificationSite,
} from '../../types/ReunificationTypes';
import {
  useApplicableSites,
  useCreateApplicableSite,
  useUpdateApplicableSite,
  useDeleteApplicableSite,
  useReunificationSites,
} from '../../hooks/queries/useReunification';

// =============================================================================
// Row Component
// =============================================================================

function ApplicableSiteRow({
  site,
  templateId,
}: {
  site: PlanApplicableSite;
  templateId: string;
}) {
  const { t } = useTranslation();
  const updateMutation = useUpdateApplicableSite(templateId);
  const deleteMutation = useDeleteApplicableSite(templateId);

  return (
    <Table.Row borderBottomWidth="1px" borderColor="border.primary">
      <Table.Cell py={3}>
        <Text fontSize="sm" color="text.primary">
          {site.site_name}
        </Text>
        <Text fontSize="xs" color="text.secondary">
          {site.site_type}
        </Text>
      </Table.Cell>
      <Table.Cell py={3}>
        <Input
          size="sm"
          type="number"
          min={0}
          width="70px"
          aria-label={`Priority order for ${site.site_name}`}
          defaultValue={site.priority_order}
          onBlur={(e) => {
            const val = parseInt(e.target.value, 10);
            if (!isNaN(val) && val !== site.priority_order) {
              updateMutation.mutate({
                siteId: site.public_id,
                data: { priority_order: val },
              });
            }
          }}
        />
      </Table.Cell>
      <Table.Cell py={3}>
        <Text fontSize="sm" color="text.primary">
          {site.capacity_limit != null ? site.capacity_limit : '--'}
        </Text>
      </Table.Cell>
      <Table.Cell py={3}>
        <IconButton
          aria-label={t('reunification.removeLocation', 'Remove location')}
          size="xs"
          variant="ghost"
          colorPalette="red"
          onClick={() => deleteMutation.mutate(site.public_id)}
        >
          <HiTrash />
        </IconButton>
      </Table.Cell>
    </Table.Row>
  );
}

// =============================================================================
// Add Sites Drawer
// =============================================================================

function AddSitesDrawer({
  isOpen,
  onClose,
  availableSites,
  isLoadingSites,
  onAdd,
}: {
  isOpen: boolean;
  onClose: () => void;
  availableSites: ReunificationSite[];
  isLoadingSites: boolean;
  onAdd: (siteIds: string[]) => void;
}) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const toggle = (value: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  const handleAdd = () => {
    onAdd(Array.from(selected));
    setSelected(new Set());
    onClose();
  };

  const handleClose = () => {
    setSelected(new Set());
    onClose();
  };

  return (
    <DrawerComponent
      isOpen={isOpen}
      onClose={handleClose}
      title={t('reunification.addLocations', 'Add Locations')}
      size="sm"
      footer={
        <SimpleGrid columns={2} gap={4} width="100%">
          <ButtonComponent
            visual="secondary"
            type="button"
            onClick={handleClose}
            data-e2e="cancel-add-sites-button"
          >
            {t('common.cancel', 'Cancel')}
          </ButtonComponent>
          <ButtonComponent
            visual="primary"
            type="button"
            onClick={handleAdd}
            disabled={selected.size === 0}
            data-e2e="confirm-add-sites-button"
          >
            {t('reunification.addWithCount', 'Add {{countText}}', {
              countText: selected.size > 0 ? `(${selected.size})` : '',
            })}
          </ButtonComponent>
        </SimpleGrid>
      }
    >
      {isLoadingSites ? (
        <Spinner size="sm" />
      ) : availableSites.length === 0 ? (
        <Text fontSize="sm" color="text.secondary">
          {t('reunification.noAdditionalLocationsAvailable', 'No additional locations available')}
        </Text>
      ) : (
        <VStack gap={3} align="stretch">
          {availableSites.map((site) => (
            <Checkbox.Root
              key={site.public_id}
              checked={selected.has(site.public_id)}
              onCheckedChange={() => toggle(site.public_id)}
              cursor="pointer"
            >
              <Checkbox.HiddenInput />
              <Checkbox.Control borderRadius="md" />
              <Checkbox.Label fontSize="sm" color="text.primary">
                {site.name}
                <Text as="span" fontSize="xs" color="text.secondary" ml={1}>
                  ({site.site_type_display})
                </Text>
              </Checkbox.Label>
            </Checkbox.Root>
          ))}
        </VStack>
      )}
    </DrawerComponent>
  );
}

// =============================================================================
// Main Section Component
// =============================================================================

interface ApplicableSitesSectionProps {
  templateId: string;
}

export function ApplicableSitesSection({
  templateId,
}: ApplicableSitesSectionProps) {
  const { t } = useTranslation();
  const { data: applicableSites, isLoading } = useApplicableSites(templateId);
  const createMutation = useCreateApplicableSite(templateId);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Fetch all active sites for the drawer picker
  const { data: sitesData, isLoading: isLoadingSites } =
    useReunificationSites({ is_active: true, page_size: 200 });

  // Filter out sites already linked to this template
  const linkedSiteIds = new Set(
    applicableSites?.map((s) => s.site) ?? [],
  );
  const availableSites = (sitesData?.results ?? []).filter(
    (s) => !linkedSiteIds.has(s.public_id),
  );

  const handleAddSites = (siteIds: string[]) => {
    for (const siteId of siteIds) {
      createMutation.mutate({ site: siteId });
    }
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={3}>
        <Text fontWeight="semibold" fontSize="sm">
          {t('reunification.applicableLocations', 'Applicable Locations')}
        </Text>
        <ButtonComponent
          visual="secondary"
          size="sm"
          type="button"
          onClick={() => setDrawerOpen(true)}
          data-e2e="add-location-button"
        >
          {t('reunification.addLocation', 'Add Location')}
        </ButtonComponent>
      </Flex>

      {isLoading ? (
        <Spinner size="sm" />
      ) : applicableSites && applicableSites.length > 0 ? (
        <Box
          borderWidth="1px"
          borderColor="border.primary"
          borderRadius="lg"
          overflow="hidden"
          bg="surface.primary"
          maxHeight="320px"
          overflowY="auto"
        >
          <Table.Root size="sm">
            <Table.Header>
              <Table.Row bg="surface.secondary">
                <Table.ColumnHeader>
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    color="text.secondary"
                    textTransform="uppercase"
                  >
                    {t('reunification.location', 'Location')}
                  </Text>
                </Table.ColumnHeader>
                <Table.ColumnHeader width="90px">
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    color="text.secondary"
                    textTransform="uppercase"
                  >
                    {t('reunification.priority', 'Priority')}
                  </Text>
                </Table.ColumnHeader>
                <Table.ColumnHeader width="100px">
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    color="text.secondary"
                    textTransform="uppercase"
                  >
                    {t('reunification.capacity', 'Capacity')}
                  </Text>
                </Table.ColumnHeader>
                <Table.ColumnHeader width="50px" />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {applicableSites.map((site) => (
                <ApplicableSiteRow
                  key={site.public_id}
                  site={site}
                  templateId={templateId}
                />
              ))}
            </Table.Body>
          </Table.Root>
        </Box>
      ) : (
        <Text fontSize="sm" color="text.secondary">
          {t('reunification.noApplicableLocationsConfigured', 'No applicable locations configured')}
        </Text>
      )}

      <AddSitesDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        availableSites={availableSites}
        isLoadingSites={isLoadingSites}
        onAdd={handleAddSites}
      />
    </Box>
  );
}

export default ApplicableSitesSection;
